/**
 * What the avatar circle is DOING — one explicit state rather than three booleans read together.
 *
 * The three booleans were `preparedPhoto !== null`, `uploadPhoto.isPending` and `photoError`, and
 * the defect they produced is the reason this module exists: the prepared bytes are stored so a
 * retry can send them again, and the retry link was offered for as long as bytes were RETAINED
 * rather than for as long as the upload had actually FAILED. The bytes are retained from the moment
 * they are cropped, so the trainer saw „Spróbuj jeszcze raz" for the whole upload and then watched
 * the photograph appear anyway.
 *
 * So the question „may this be retried?" is answered by the PHASE, and the bytes are only what a
 * retry would send. `idle → preparing → uploading → idle` is the whole happy path; `failed` is
 * reachable from either working state and is the only state that offers a retry.
 */

/** Bytes a retry can send again, kept across a failure so the trainer need not find the file. */
export interface AvatarState {
  readonly phase: 'idle' | 'preparing' | 'uploading' | 'failed';
  readonly prepared: Blob | null;
}

export type AvatarEvent =
  | { readonly kind: 'pick' }
  | { readonly kind: 'prepared'; readonly blob: Blob }
  | { readonly kind: 'prepareFailed' }
  | { readonly kind: 'uploaded' }
  | { readonly kind: 'uploadFailed' }
  | { readonly kind: 'retry' };

export const AVATAR_IDLE: AvatarState = { phase: 'idle', prepared: null };

export function nextAvatarState(state: AvatarState, event: AvatarEvent): AvatarState {
  switch (event.kind) {
    case 'pick':
      // A new file discards the old bytes outright: retrying the PREVIOUS photograph after picking
      // a new one would upload the one the trainer just replaced.
      return { phase: 'preparing', prepared: null };
    case 'prepared':
      return { phase: 'uploading', prepared: event.blob };
    case 'prepareFailed':
      // Nothing was ever encoded, so there is nothing to send again — the trainer picks another file.
      return { phase: 'failed', prepared: null };
    case 'uploaded':
      return AVATAR_IDLE;
    case 'uploadFailed':
      return { phase: 'failed', prepared: state.prepared };
    case 'retry':
      return state.prepared === null ? state : { phase: 'uploading', prepared: state.prepared };
  }
}

/** The bytes „Spróbuj jeszcze raz" would send, or `null` when the link must not be drawn at all. */
export function avatarRetryBytes(state: AvatarState): Blob | null {
  return state.phase === 'failed' ? state.prepared : null;
}

/** Whether the circle is working — the loader stands in it and the camera is refused. */
export function avatarIsWorking(state: AvatarState): boolean {
  return state.phase === 'preparing' || state.phase === 'uploading';
}
